import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Protocol } from '@uniswap/router-sdk'
import { isUniswapXSupportedChain } from 'constants/chains'
import ms from 'ms'
import {
  ClassicAPIConfig,
  GetQuoteArgs,
  INTERNAL_ROUTER_PREFERENCE_PRICE,
  QuoteIntent,
  QuoteMethod,
  QuoteState,
  RouterPreference,
  RoutingConfig,
  TradeResult,
  URAQuoteResponse,
  URAQuoteType,
  UniswapXConfig,
  UniswapXv2Config,
} from 'state/routing/types'
import { isExactInput, transformQuoteToTrade } from 'state/routing/utils'
import { logSwapQuoteRequest } from 'tracing/swapFlowLoggers'
import { trace } from 'tracing/trace'
import { InterfaceEventNameLocal } from 'uniswap/src/features/telemetry/constants'
import { sendAnalyticsEvent } from 'uniswap/src/features/telemetry/send'
import { logger } from 'utilities/src/logger/logger'

const UNISWAP_GATEWAY_DNS_URL = process.env.REACT_APP_UNISWAP_GATEWAY_DNS
if (UNISWAP_GATEWAY_DNS_URL === undefined) {
  throw new Error(`UNISWAP_GATEWAY_DNS_URL must be defined environment variables`)
}

const CLIENT_PARAMS = {
  protocols: [Protocol.V2, Protocol.V3, Protocol.MIXED],
}

const protocols: Protocol[] = [Protocol.V2, Protocol.V3, Protocol.MIXED]

// routing API quote query params: https://github.com/Uniswap/routing-api/blob/main/lib/handlers/quote/schema/quote-schema.ts
const DEFAULT_QUERY_PARAMS = {
  // this should be removed once BE fixes issue where enableUniversalRouter is required for fees to work
  enableUniversalRouter: true,
}

function getRoutingAPIConfig(args: GetQuoteArgs): RoutingConfig {
  const {
    account,
    tokenInChainId,
    uniswapXForceSyntheticQuotes,
    routerPreference,
    protocolPreferences,
    isXv2,
    priceImprovementBps,
    forceOpenOrders,
    deadlineBufferSecs,
    isXv2Arbitrum,
  } = args

  const uniswapX: UniswapXConfig = {
    useSyntheticQuotes: uniswapXForceSyntheticQuotes,
    swapper: account,
    routingType: URAQuoteType.DUTCH_V1,
  }

  const uniswapXv2: UniswapXv2Config = {
    useSyntheticQuotes: uniswapXForceSyntheticQuotes,
    swapper: account,
    routingType: URAQuoteType.DUTCH_V2,
    ...(isXv2Arbitrum
      ? {
          priceImprovementBps,
          forceOpenOrders,
          deadlineBufferSecs,
        }
      : {}),
  }

  const classic: ClassicAPIConfig = {
    ...DEFAULT_QUERY_PARAMS,
    protocols: protocolPreferences && protocolPreferences.length > 0 ? protocolPreferences : protocols,
    routingType: URAQuoteType.CLASSIC,
    recipient: account,
    enableFeeOnTransferFeeFetching: true,
  }

  if (
    // If the user has opted out of UniswapX during the opt-out transition period, we should respect that preference and only request classic quotes.
    routerPreference === RouterPreference.API ||
    routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE ||
    (!isUniswapXSupportedChain(tokenInChainId) && !isXv2Arbitrum)
  ) {
    return [classic]
  }

  return [isXv2 || isXv2Arbitrum ? uniswapXv2 : uniswapX, classic]
}

export const routingApi = createApi({
  reducerPath: 'routingApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (build) => ({
    getQuote: build.query<TradeResult, GetQuoteArgs>({
      queryFn(args, _api, _extraOptions, fetch) {
        return trace({ name: 'Quote', op: 'quote', data: { ...args } }, async (trace) => {
          logSwapQuoteRequest(args.tokenInChainId, args.routerPreference, false)
          const {
            tokenInAddress: tokenIn,
            tokenInChainId,
            tokenOutAddress: tokenOut,
            tokenOutChainId,
            amount,
            tradeType,
            sendPortionEnabled,
          } = args

          const requestBody = {
            tokenInChainId,
            tokenIn,
            tokenOutChainId,
            tokenOut,
            amount,
            sendPortionEnabled,
            type: isExactInput(tradeType) ? 'EXACT_INPUT' : 'EXACT_OUTPUT',
            intent:
              args.routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE ? QuoteIntent.Pricing : QuoteIntent.Quote,
            configs: getRoutingAPIConfig(args),
            useUniswapX: args.routerPreference === RouterPreference.X,
            swapper: args.account,
          }

          // NOTE: Server-side routing API call disabled — use client-side SOR directly to avoid CORS/DNS issues
          // try {
          //   return await trace.child({ name: 'Quote on server', op: 'quote.server' }, async () => {
          //     const response = await fetch({
          //       method: 'POST',
          //       url: `${UNISWAP_GATEWAY_DNS_URL}/quote`,
          //       body: JSON.stringify(requestBody),
          //       headers: { 'x-request-source': 'uniswap-web' },
          //     })
          //     if (response.error) {
          //       const errorData = response.error.data as { errorCode?: string; detail?: string }
          //       if (typeof errorData === 'object' && (errorData?.errorCode === 'NO_ROUTE' || errorData?.detail === 'No quotes available')) {
          //         return { data: { state: QuoteState.NOT_FOUND, latencyMs: trace.now() } }
          //       }
          //       throw response.error
          //     }
          //     const uraQuoteResponse = response.data as URAQuoteResponse
          //     const tradeResult = await transformQuoteToTrade(args, uraQuoteResponse, QuoteMethod.ROUTING_API)
          //     return { data: { ...tradeResult, latencyMs: trace.now() } }
          //   })
          // } catch (error: any) {
          //   logger.warn('routing/slice', 'queryFn', `GetQuote failed on Unified Routing API, falling back to client: ${error?.message ?? error?.detail ?? error}`)
          // }

          try {
            return trace.child({ name: 'Quote on client', op: 'quote.client' }, async () => {
              const { getRouter, getClientSideQuote } = await import('lib/hooks/routing/clientSideSmartOrderRouter')
              const router = getRouter(args.tokenInChainId)
              const quoteResult = await getClientSideQuote(
                args,
                router,
                args.protocolPreferences ? { protocols: args.protocolPreferences } : CLIENT_PARAMS,
              )
              if (quoteResult.state === QuoteState.SUCCESS) {
                const trade = await transformQuoteToTrade(args, quoteResult.data, QuoteMethod.CLIENT_SIDE_FALLBACK)
                return {
                  data: { ...trade, latencyMs: trace.now() },
                }
              } else {
                return { data: { ...quoteResult, latencyMs: trace.now() } }
              }
            })
          } catch (error: any) {
            logger.warn('routing/slice', 'queryFn', `GetQuote failed on client: ${error}`)
            trace.setError(error)
            return {
              error: { status: 'CUSTOM_ERROR', error: error?.detail ?? error?.message ?? error },
            }
          }
        })
      },
      keepUnusedDataFor: ms(`10s`),
      extraOptions: {
        maxRetries: 0,
      },
    }),
  }),
})

export const { useGetQuoteQuery } = routingApi
export const useGetQuoteQueryState = routingApi.endpoints.getQuote.useQueryState

export const jocPriceAPi = createApi({
  reducerPath: 'jocPriceApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (build) => ({
    getJocPrice: build.query<{ prices: number[] }, { symbol: string; chainId: string; tokenAddress: string }[]>({
      queryFn(args, _api, _extraOptions, fetch) {
        return trace({ name: 'JocPrice', op: 'jocPrice.get', data: { ...args } }, async (trace) => {
          const baseUrl = 'https://api.gu.net/v1'
          try {
            return await trace.child({ name: 'JocPrice', op: 'jocPrice.get' }, async () => {
              const response = await fetch({
                method: 'GET',
                url: `${baseUrl}/prices/crypto`,
                params: {
                  fsymbols: args.map((arg) => arg.symbol).join(','),
                  tsymbols: 'USD',
                  types: 'evm',
                  chainIds: args.map((arg) => arg.chainId).join(','),
                  tokenAddresses: args.map((arg) => arg.tokenAddress).join(','),
                },
              })

              if (response.error) {
                throw response.error
              }
              return { data: { prices: (response.data as any).map((item: any) => item.price) }, latencyMs: trace.now() }
            })
          } catch (error: any) {
            logger.warn('jocPrice/slice', 'queryFn', `GetJocPrice failed: ${error?.message ?? error?.detail ?? error}`)
            trace.setError(error)
            return {
              error: { status: 'CUSTOM_ERROR', error: error?.detail ?? error?.message ?? error },
            }
          }
        })
      },
      keepUnusedDataFor: ms(`10s`),
      extraOptions: {
        maxRetries: 0,
      },
    }),
  }),
})

export const { useGetJocPriceQuery } = jocPriceAPi
export const useGetJocPriceQueryState = jocPriceAPi.endpoints.getJocPrice.useQueryState

export type AlchemyPayCryptoListResponse = {
  symbol: string
  chainId: string
  type: string
  crypto: string
  address: string
  network: string
  icon: string
  buyEnable: number
  sellEnable: number
  minPurchaseAmount: number
  maxPurchaseAmount: number
  minSellAmount: number | null
  maxSellAmount: number | null
  timestamp: string
}

export type GetAlchemyPayBuyUrlParams = {
  tokenAddress?: string;
  chainId?: string;
  network?: string | number;
  merchantOrderNo?: string;
  fiat?: string;
  fiatAmount?: string;
  crypto?: string;
  address?: string;
  memo?: string;
  email?: string;
  token?: string;
  language?: string;
  showTable?: string;
  redirectUrl?: string;
  callbackUrl?: string;
  merchantName?: string;
  displayAddress?: string;
};

export const alchemyPayAggregatorApi = createApi({
  reducerPath: 'alchemyPayAggregatorApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (build) => ({
    getAlchemyPayCryptoList: build.query<AlchemyPayCryptoListResponse[], void>({
      queryFn(_, _api, _extraOptions, fetch) {
        return trace({ name: 'AlchemyPay', op: 'alchemyPay.getCryptoList' }, async (trace: any) => {
          const baseUrl = 'https://api.gu.net/v1'
          try {
            return await trace.child({ name: 'AlchemyPay', op: 'alchemyPay.getCryptoList' }, async () => {
              const response = await fetch({
                method: 'GET',
                url: `${baseUrl}/payment-providers/alchemy-pay/crypto/list`,
              })

              if (response.error) {
                throw response.error
              }
              return { data: response.data as AlchemyPayCryptoListResponse[], latencyMs: trace.now() }
            })
          } catch (error: any) {
            trace.setError(error)
            return {
              error: { status: 'CUSTOM_ERROR', error: error?.detail ?? error?.message ?? error },
            }
          }
        })
      },
      keepUnusedDataFor: ms(`10s`),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    getAlchemyPayBuyUrl: build.query<{ url: string }, GetAlchemyPayBuyUrlParams>({
      queryFn(params, _api, _extraOptions, fetch) {
        return trace({ name: 'AlchemyPay', op: 'alchemyPay.getBuyUrl', data: { ...params } }, async (trace: any) => {
          const baseUrl = 'https://api.gu.net/v1'
          try {
            return await trace.child({ name: 'AlchemyPay', op: 'alchemyPay.getBuyUrl' }, async () => {
              const response = await fetch({
                method: 'GET',
                url: `${baseUrl}/payment-providers/alchemy-pay/crypto/buy`,
                params: params,
              })

              if (response.error) {
                throw response.error
              }
              return { data: { url: (response.data as any).url }, latencyMs: trace.now() }
            })
          } catch (error: any) {
            trace.setError(error)
            return {
              error: { status: 'CUSTOM_ERROR', error: error?.detail ?? error?.message ?? error },
            }
          }
        })
      },
      keepUnusedDataFor: ms(`10s`),
      extraOptions: {
        maxRetries: 0,
      },
    }),
  }),
})

export const { useGetAlchemyPayCryptoListQuery } = alchemyPayAggregatorApi
export const useGetAlchemyPayCryptoListQueryState = alchemyPayAggregatorApi.endpoints.getAlchemyPayCryptoList.useQueryState
export const { useGetAlchemyPayBuyUrlQuery } = alchemyPayAggregatorApi
export const useGetAlchemyPayBuyUrlQueryState = alchemyPayAggregatorApi.endpoints.getAlchemyPayBuyUrl.useQueryState